import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PricingTeaser from "@/components/PricingTeaser";
import CalculatorTile from "@/components/CalculatorTile";
import {
  Calculator,
  FileText,
  Car,
  Gauge,
  Percent,
  Split,
  Wallet,
  Users,
  Receipt,
  Hammer,
  ArrowRight,
  TrendingUp,
} from "lucide-react";

const tiles = [
  { to: "/app/calc/gst", icon: Calculator, title: "GST", sub: "Add / remove 15%" },
  { to: "/app/calc/quote", icon: FileText, title: "Quote / Invoice", sub: "Labour + materials" },
  { to: "/app/calc/margin", icon: Percent, title: "Margin vs Markup", sub: "Don't mix them up" },
  { to: "/app/calc/expense", icon: Split, title: "Business or Personal", sub: "What can you claim?" },
  { to: "/app/calc/budget", icon: Wallet, title: "Budget", sub: "Plan every dollar" },
  { to: "/uber-worth-it", icon: Car, title: "Is Uber Driving Worth It?", sub: "Uber real $/hr after tax", external: true },
  { to: "/app/calc/mileage", icon: Car, title: "Mileage", sub: "IRD rate × km" },
  { to: "/app/calc/tax", icon: Gauge, title: "Tax / PAYE", sub: "New brackets · 1 Apr 2025" },
];

function Stat({ label, value, accent }) {
  return (
    <div className="card-sharp p-6">
      <div className="label-mono">{label}</div>
      <div
        className={`font-mono text-3xl mt-3 ${accent ? "text-[var(--primary)]" : "text-white"}`}
      >
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/summary").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="stagger-in">
      <div className="flex items-start justify-between gap-8 flex-wrap">
        <div>
          <div className="label-mono">/ DASHBOARD</div>
          <h1 className="font-display text-5xl tracking-tight mt-2">
            Kia ora, {user?.name || "tradie"}.
          </h1>
          <p className="text-zinc-400 mt-2">
            Jump into a calculator or pick up where you left off.
          </p>
        </div>
        <Link to="/app/calc/quote" className="btn-primary" data-testid="dashboard-new-quote">
          <Hammer size={14} /> New quote
        </Link>
      </div>

      {/* Tiles — primary CTA, big and tappable */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mt-8"
        data-testid="dashboard-calc-grid"
      >
        {tiles.map((t) => (
          <CalculatorTile
            key={t.to}
            to={t.to}
            icon={t.icon}
            title={t.title}
            sub={t.sub}
            testId={`tile-${t.title.toLowerCase().replace(/[^a-z]/g, "")}`}
            emphasised
            external={t.external}
          />
        ))}
      </div>

      {/* Stats */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={14} className="text-[var(--primary)]" />
          <div className="label-mono">/ YOUR NUMBERS</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Clients" value={stats?.clients ?? "—"} />
          <Stat label="Saved quotes" value={stats?.jobs ?? "—"} />
          <Stat label="Invoices" value={stats?.invoices ?? "—"} />
          <Stat label="Mileage km" value={stats?.mileage_km ?? "—"} />
          <Stat label="Unpaid" value={fmtMoney(stats?.unpaid_total ?? 0)} accent />
          <Stat label="Paid" value={fmtMoney(stats?.paid_total ?? 0)} />
          <Stat label="Mileage $" value={fmtMoney(stats?.mileage_amount ?? 0)} />
          <div className="card-sharp p-6 flex flex-col justify-between">
            <div>
              <div className="label-mono">Quick links</div>
            </div>
            <div className="space-y-2 text-xs font-mono">
              <Link
                to="/app/clients"
                className="flex items-center gap-2 text-zinc-300 hover:text-[var(--primary)]"
                data-testid="quicklink-clients"
              >
                <Users size={12} /> Manage clients
              </Link>
              <Link
                to="/app/invoices"
                className="flex items-center gap-2 text-zinc-300 hover:text-[var(--primary)]"
                data-testid="quicklink-invoices"
              >
                <Receipt size={12} /> View invoices
              </Link>
            </div>
          </div>
        </div>
      </div>

      <PricingTeaser />
    </div>
  );
}
